import React from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Check, ArrowForward } from '@mui/icons-material';
import { SectionTitle } from '../common';
import { fadeInUp, staggerContainer, cardHover, viewportConfig } from '../../utils/animations';
import { PRICING_TIERS, PEGASUS_URLS } from '../../utils/constants';

const Pricing = () => {
  const handleExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      id="pricing"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <SectionTitle
          overline="Pricing"
          title="Simple, Transparent Pricing"
          subtitle="Your agent charges KES 25,000/month for a 10-unit building. IT-Orama starts free."
        />

        <Box
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={staggerContainer}
        >
          <Grid container spacing={3} alignItems="stretch">
            {PRICING_TIERS.map((tier, index) => (
              <Grid item xs={12} sm={6} md={4} key={tier.id}>
                <Box
                  component={motion.div}
                  variants={fadeInUp}
                  custom={index}
                  initial="rest"
                  whileHover="hover"
                  animate="rest"
                  sx={{ height: '100%' }}
                >
                  <Card
                    component={motion.div}
                    variants={cardHover}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      border: '2px solid',
                      borderColor: tier.popular ? 'secondary.main' : 'grey.100',
                      backgroundColor: 'background.paper',
                      overflow: 'visible',
                      cursor: 'pointer',
                    }}
                  >
                    {tier.popular && (
                      <Chip
                        label="Most Popular"
                        color="secondary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -14,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontWeight: 700,
                          px: 1,
                        }}
                      />
                    )}
                    <CardContent
                      sx={{
                        p: 3,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
                      >
                        {tier.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', mb: 2 }}
                      >
                        {tier.unitLimit}
                      </Typography>

                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: tier.popular ? 'secondary.main' : 'primary.main',
                          mb: 3,
                        }}
                      >
                        {tier.priceLabel}
                      </Typography>

                      <Box sx={{ flexGrow: 1, mb: 3 }}>
                        {tier.features.map((feature) => (
                          <Box
                            key={feature}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <Check
                              sx={{
                                fontSize: 18,
                                color: 'secondary.main',
                                mt: 0.3,
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Button
                        variant={tier.popular ? 'contained' : 'outlined'}
                        color={tier.popular ? 'secondary' : 'primary'}
                        fullWidth
                        endIcon={<ArrowForward />}
                        onClick={() => handleExternalLink(PEGASUS_URLS.signup)}
                        sx={{ py: 1.2, mt: 'auto' }}
                      >
                        {tier.cta}
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Typography
          component={motion.p}
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          variant="body2"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mt: 4,
          }}
        >
          All packages include a free trial month. No credit card required.
        </Typography>
      </Container>
    </Box>
  );
};

export default Pricing;
